import { useCallback, useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'

import {
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import ArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineUploadIcon from 'LuxKit/Icons/line/LineUploadIcon'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import Group from 'components/utils/Group'
import GeoContext from 'contexts/geoContext'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import noop from 'lib/function/noop'
import { copyValueToClipboard } from 'lib/web/clipboardUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import {
  TripPlannerStickyHeader,
  TripPlannerHeaderCentreContent,
  TripPlannerStyledLogo,
  FlexBreakpoint,
  LeftContent,
  RightContent,
} from 'tripPlanner/components/Header/Shared'
import ImmersiveHeaderTripDetails from 'tripPlanner/components/ImmersiveHeader/ImmersiveHeaderTripDetails'
import { HeaderInViewContext } from 'tripPlanner/contexts/HeaderInViewContext'
import type { PublicFullTrip } from 'tripPlanner/types/publicTrip'
import { getClipboardUrl } from 'tripPlanner/utils/publicTrip'

const mapState = (state: App.State, ownProps: Props) => ({
  ...ownProps,
  headlessMode: state.system.headlessMode,
  isLoggedIn: selectLoggedIn(state),
})

const connector = connect(mapState)

interface Props {
  publicTrip: PublicFullTrip
}

const trackTripLoginModalSignUpView = () => {
  fireInteractionEvent(tripLoginModalSignUpView('public_trip_sign_in'))
}
const trackTripLoginModalSignUpDismiss = () => {
  fireInteractionEvent(tripLoginModalSignUpDismiss('public_trip_sign_in'))
}

function PublicTripPageHeader({
  publicTrip,
  headlessMode,
  isLoggedIn,
}: ReturnType<typeof mapState>) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const rootRef = useRef<HTMLElement>(null)
  useHeaderStateObserver('tripPlanner', rootRef)

  // const openModal = useOpenModal()

  const login = usePendingLoginHandler(
    noop,
    'tripPlannerLogin',
    trackTripLoginModalSignUpView,
    trackTripLoginModalSignUpDismiss,
  )

  // const openShareModal = useCallback(() => {
  //   openModal(PublicShareTrip, { trip: publicTrip })
  // }, [openModal, publicTrip])

  const copiedToClipboardMessage = intl.formatMessage({
    id: 'Dwd5G-i-Y5iNFXQil44E',
    defaultMessage: 'Copied to clipboard',
    description: 'Snackbar message shown after copying a trip link to clipboard',
  })

  const copyLink = useCallback(() => {
    const url = getClipboardUrl(currentRegionCode, true, '', publicTrip.id)
    copyValueToClipboard(url)
    showSnackbar(copiedToClipboardMessage, 'success')
  }, [publicTrip, currentRegionCode, copiedToClipboardMessage])

  const { inView: headerInView } = useContext(HeaderInViewContext)

  const allTripsLabel = intl.formatMessage({
    id: 'gJStHX_TbjeA7nQRIGfO',
    defaultMessage: 'All Trips',
    description: 'Navigation link text to go back to all trips list from public trip view',
  })

  const shareLabel = intl.formatMessage({
    id: 'tKGEZY6mFeWUjY9vvLfS',
    defaultMessage: 'Share',
    description: 'Button text and aria-label to share a public trip in the trip planner',
  })

  const logInLabel = intl.formatMessage({
    id: 'hUTrfc1IS_xYeAKuHfM_',
    defaultMessage: 'Log in',
    description: 'Button text to log in from public trip page header',
  })

  return (
    <TripPlannerStickyHeader ref={rootRef}>
      {headerInView && !headlessMode && (
        <TextButton
          kind="tertiary"
          variant="dark"
          size="large"
          startIcon={<ArrowLeftIcon />}
          to="/trip-planner"
        >
          {allTripsLabel}
        </TextButton>
      )}
      {(!headerInView || headlessMode) && (
        <LeftContent>
          {!headlessMode && (
            <IconButton
              kind="tertiary"
              variant="dark"
              size="large"
              to="/trip-planner"
              aria-label={allTripsLabel}
            >
              <ArrowLeftIcon />
            </IconButton>
          )}
          <ImmersiveHeaderTripDetails trip={publicTrip} dateFormat="dates" />
        </LeftContent>
      )}

      <TripPlannerHeaderCentreContent to="/">
        {headerInView && !headlessMode && (
          <FlexBreakpoint max="tablet">
            <TripPlannerStyledLogo />
          </FlexBreakpoint>
        )}
        <FlexBreakpoint min="desktop">
          <TripPlannerStyledLogo />
        </FlexBreakpoint>
      </TripPlannerHeaderCentreContent>
      <RightContent>
        <Group
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="center"
          gap={8}
        >
          {!isLoggedIn && (
            <TextButton
              kind="secondary"
              variant="default"
              shape="square"
              size="medium"
              aria-label={shareLabel}
              onClick={login}
            >
              {logInLabel}
            </TextButton>
          )}
          <TextButton
            kind="tertiary"
            variant="default"
            shape="square"
            size="medium"
            startIcon={<LineUploadIcon />}
            aria-label={shareLabel}
            onClick={copyLink}
          >
            {shareLabel}
          </TextButton>
        </Group>
      </RightContent>
    </TripPlannerStickyHeader>
  )
}

export default connector(PublicTripPageHeader)
